import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FounderSection = () => {
  const navigate = useNavigate();

  const founderData = {
    name: "Mark McEvoy",
    title: "Founder & Chief Data Strategist",
    credentials: "Proven Track Record Delivering Value from Data",
    experience: "30+ years",
    image: "/assets/images/mark-1768823261480.jpg",
    story: `After witnessing countless organizations struggle with data transformation initiatives that focused on technology rather than strategy, I founded Northstar Analytics with a simple belief: sustainable data success requires executive-level thinking at every organizational level.\n\nMy journey began in the technology industry, where I saw brilliant technical implementations fail because teams weren't equipped to think strategically about data. The missing piece wasn't better tools—it was better thinking.\n\nNorthstar Analytics bridges this gap by combining fractional executive expertise with comprehensive team development, ensuring your organization doesn't just implement solutions, but develops the strategic mindset to sustain and evolve them.`,
    philosophy: [
      "Data strategy is business strategy—they cannot be separated",
      "Sustainable transformation requires mindset change, not just tool change",
      "Every team member should think like a Chief Data Officer",
      "Fractional expertise delivers executive impact without executive overhead"
    ],
    achievements: [
      {
        icon: "Target",
        title: "Strategic Transformations",
        description: "Led 50+ successful data strategy implementations across various industries"
      },
      {
        icon: "Users",
        title: "Team Development",
        description: "Trained 200+ professionals in strategic data thinking and implementation"
      },
      {
        icon: "Award",
        title: "Industry Recognition",
        description: "Recognized expert in data governance and fractional executive services"
      },
      {
        icon: "TrendingUp",
        title: "ROI Excellence",
        description: "Achieved average 300% ROI for client data transformation projects"
      }
    ]
  };

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Compass" size={24} className="text-primary" />
            </div>
            <span className="text-primary font-semibold text-lg">Leadership Philosophy</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Meet Your Data Strategy Guide
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Strategic vision meets practical implementation through proven leadership and deep industry expertise.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Founder Profile */}
          <div className="space-y-8">
            {/* Profile Card */}
            <div className="bg-surface rounded-2xl p-8 hover-lift">
              <div className="flex items-start space-x-6">
                <div className="relative">
                  <Image
                    src={founderData?.image}
                    alt={founderData?.name}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={16} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-primary mb-2">{founderData?.name}</h3>
                  <p className="text-lg font-semibold text-secondary mb-2">{founderData?.title}</p>
                  <p className="text-sm text-muted-foreground mb-4">{founderData?.credentials}</p>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium">{founderData?.experience}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium">Dublin, Ireland</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Achievements Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {founderData?.achievements?.map((achievement, index) => (
                <div key={index} className="bg-white border border-border rounded-xl p-6 hover-lift">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={achievement?.icon} size={20} className="text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">{achievement?.title}</h4>
                      <p className="text-sm text-muted-foreground">{achievement?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold mb-2">Connect with Mark</h4>
                  <p className="text-white/80 text-sm">Discuss your data strategy challenges directly</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => navigate('/contact-multi-channel-engagement-hub')}
                >
                  Schedule Call
                </Button>
              </div>
            </div>
          </div>

          {/* Story & Philosophy */}
          <div className="space-y-8">
            {/* Founder Story */}
            <div>
              <h3 className="text-2xl font-bold text-primary mb-6">The Northstar Story</h3>
              <div className="prose prose-lg max-w-none">
                {founderData?.story?.split('\n\n')?.map((paragraph, index) => (
                  <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Philosophy */}
            <div className="bg-surface rounded-xl p-8">
              <h4 className="text-xl font-bold text-primary mb-6 flex items-center">
                <Icon name="Lightbulb" size={24} className="mr-3" />
                Core Philosophy
              </h4>
              <div className="space-y-4">
                {founderData?.philosophy?.map((principle, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={14} className="text-secondary" />
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{principle}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <div className="relative bg-primary/5 rounded-xl p-8 border-l-4 border-secondary">
              <Icon name="Quote" size={32} className="text-secondary/30 absolute top-4 right-4" />
              <blockquote className="text-lg italic text-primary leading-relaxed">
                "True data transformation isn't about implementing the latest technology—it's about elevating how your entire organization thinks about and leverages data as a strategic asset."
              </blockquote>
              <cite className="block mt-4 text-sm font-semibold text-secondary">
                — {founderData?.name}, Founder
              </cite>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;